<template>
  <Slideover
:title="`Add Stack - ${item?.ITEMNO || 'Unknown Item'}`" :open="open" :actions="[
    {
      text: 'Save',
      theme: 'primary',
      disabled: isSubmitting || !store.isOnline || !form.Quantity,
      method: handleSubmit
    }
  ]" @close="close">
    <form class="space-y-6" @submit.prevent="handleSubmit">
      <!-- Item Information Display -->
      <div class="bg-gray-50 p-4 rounded-md">
        <h3 class="text-sm font-medium text-gray-700 mb-2">Adding Stack:</h3>
        <div class="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span class="font-medium">Item No:</span> {{ item?.ITEMNO }}
          </div>
          <div>
            <span class="font-medium">Area:</span> {{ item?.AREA }}
          </div>
          <div class="col-span-2">
            <span class="font-medium">Description:</span> {{ item?.MEMO || 'N/A' }}
          </div>
        </div>
      </div>

      <!-- Quantity Input -->
      <div>
        <label class="block text-sm font-medium text-gray-700">Quantity</label>
        <input
v-model.number="form.Quantity" type="number"
          class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          placeholder="Enter quantity for cloned item (optional)" />
        <p class="mt-1 text-sm text-gray-500">
          Enter the quantity for the new cloned item (can be left empty)
        </p>
      </div>
    </form>
  </Slideover>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import Slideover from '@/components/partials/Slideover.vue'
import useStockTake from '@/stores/stocktakestore'
import useToasterStore from '@/stores/toaster'
import type { StockItem } from '@/data/schema/stocktake'

interface Props {
  open: boolean
  item: StockItem | null
}

const props = defineProps<Props>()
const emit = defineEmits(['close'])

const store = useStockTake()
const toaster = useToasterStore()
const isSubmitting = ref(false)

// Form data
const form = ref({
  Quantity: null as number | null,
})

// Reset form when item changes
watch(() => props.item, () => {
  resetForm()
})

// Stop polling when form opens, restart when closes
watch(() => props.open, (newOpen) => {
  store.setFormOpen(newOpen)
  if (newOpen) {
    store.stopPolling()
    resetForm()
  } else {
    store.startPolling()
  }
})

function resetForm() {
  form.value = {
    Quantity: null as number | null,
  }
}

async function handleSubmit() {
  if (isSubmitting.value || !store.isOnline || !props.item) return

  isSubmitting.value = true

  try {
    const depot = store.selectedDepot.prefix.toUpperCase()

    // Prepare item data for cloning
    const itemData = {
      ...Object.fromEntries(
        Object.entries(props.item).filter(([k, v]) => v !== null && k !== 'id' && k !== 'STACKNO' && k !== 'Quantity' && k !== 'Recount')
      ),
      Quantity: form.value.Quantity,
      Status: "1",
      AREA: store.enteredArea,
      First_Counter: store.counter1,
      Second_Counter: store.counter2,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    }

    const newItem = await store.duplicateStockItem(String(depot), itemData)

    // Set syncing status for the new item if we have its ID
    let newItemId = null
    if (newItem?.data?.id) {
      newItemId = newItem.data.id
      await store.setSyncingItem(newItemId, true)
    }

    // Show success popup
    if (newItem?.data) {
      const createdItem = newItem.data
      toaster.success({
        text: `Item cloned successfully - Item No: ${createdItem.ITEMNO}, Quantity: ${form.value.Quantity}`,
      })
    }

    // Clear syncing status after a short delay to show the sync completed
    if (newItemId) {
      setTimeout(() => {
        store.setSyncingItem(newItemId, false)
      }, 2000)
    }

    // Close the form
    close()
  } catch (err: any) {
    toaster.error({
      text: err.message || 'Failed to clone item. Please try again.',
    })
  } finally {
    isSubmitting.value = false
  }
}

function close() {
  emit('close')
}
</script>
